import type { Schema } from '@/shared/api/contract';
import { api } from '@/shared/api/client';

import type {
  LoginPayload,
  LoginResult,
  PasswordChange,
  RecoveryCodes,
  SecondFactorVerify,
  TwoFactorConfirm,
  TwoFactorEnabled,
  TwoFactorSetup,
  TwoFactorStatus,
  User,
} from './types';

/** Query key of the current session. Everything auth related is invalidated through it. */
export { sessionQueryKey } from '@/shared/session/currentUser';

/** The second factor of one's own account (contract v10.45). Not under `sessionQueryKey`:
 * renewing the session must not ask for the QR again. */
export const twoFactorKeys = {
  status: ['auth', 'two-factor', 'status'] as const,
  setup: ['auth', 'two-factor', 'setup'] as const,
};

export const authApi = {
  /**
   * Sets the httpOnly cookie. The token never reaches JavaScript. Since contract v10.45 the
   * answer may say `second_factor`: then the cookie is a pending one and the session opens
   * only after `verifySecondFactor` or `confirmTwoFactor`.
   */
  login: (payload: LoginPayload): Promise<LoginResult> =>
    api.post<LoginResult>('/api/auth/login', payload),

  /** The code of the app or one recovery code, never both. Answers the open session. */
  verifySecondFactor: (payload: SecondFactorVerify): Promise<User> =>
    api.post<User>('/api/auth/two-factor/verify', payload),

  /** The QR and the secret. Idempotent while not confirmed: the same secret every time, so
   * reopening the screen does not break an app that already scanned it. Works with the
   * pending cookie of a login and with an open session (the profile). */
  startTwoFactorSetup: (): Promise<TwoFactorSetup> =>
    api.post<TwoFactorSetup>('/api/auth/two-factor/setup'),

  /** The first code of the app: turns the second factor on and answers the ten recovery
   * codes, shown once, with the session (opened here when it came from a login). */
  confirmTwoFactor: (payload: TwoFactorConfirm): Promise<TwoFactorEnabled> =>
    api.post<TwoFactorEnabled>('/api/auth/two-factor/setup/confirm', payload),

  twoFactorStatus: (): Promise<TwoFactorStatus> =>
    api.get<TwoFactorStatus>('/api/profile/two-factor'),

  /** Ten new recovery codes; the old ones stop working. Asks for the password again. */
  renewRecoveryCodes: (password: string): Promise<RecoveryCodes> =>
    api.post<RecoveryCodes>('/api/profile/two-factor/recovery-codes', { password }),

  forgetDevice: (id: string): Promise<void> =>
    api.delete<void>(`/api/profile/two-factor/devices/${encodeURIComponent(id)}`),

  logout: (): Promise<void> => api.post<void>('/api/auth/logout'),

  // `GET /api/auth/me` lives in `sessionQueryOptions` (shared/session): the guard and
  // every reader of the session share that one request and its options.

  changePassword: (payload: PasswordChange): Promise<User> =>
    api.post<User>('/api/profile/password', payload),

  /** The signature of the mail manager (contract v10.41). Empty removes it. Answers the
   * session with the new one. */
  changeSignature: (signature: string): Promise<User> =>
    api.put<User>('/api/profile/signature', { signature }),

  /** Who an invitation is for and how long it has left, without burning it. 404 when it
   * does not exist, was used or expired. */
  peekInvite: (token: string): Promise<Schema<'InvitePeek'>> =>
    api.get<Schema<'InvitePeek'>>(`/api/auth/invite/${encodeURIComponent(token)}`),

  /** Does not open a session (the person logs in afterwards with the new password),
   * so the API answers plain `UserRead`, not `SessionRead`. */
  acceptInvite: (token: string, password: string): Promise<Schema<'UserRead'>> =>
    api.post<Schema<'UserRead'>>(`/api/auth/invite/${encodeURIComponent(token)}`, { password }),
};
